/**
 * Recovery from an unrecoverable Angular service-worker state.
 *
 * The service worker serves index.html and every bundle from its own cache.
 * When it ends up holding files from two different builds (a rolling update
 * across replicas), Angular emits `SwUpdate.unrecoverable` — and the page it
 * serves may never start. A reload is the documented recovery.
 *
 * Reload at most once per window: if the state survives the reload, looping
 * would lock the operator out of the app. After one attempt we stop and the
 * existing portal message tells them to reload by hand.
 */
export const SW_RELOAD_KEY = 'sw-unrecoverable-reloaded-at';

export function shouldReloadForUnrecoverable(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined,
  now: number,
  windowMs = 60_000
): boolean {
  return shouldReloadOnce(storage, SW_RELOAD_KEY, now, windowMs);
}

/**
 * True at most once per `windowMs` for a given key, recorded in `storage`.
 * Shared by the service-worker and the chunk-load recoveries so neither can
 * put the page into a reload loop.
 */
export function shouldReloadOnce(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined,
  key: string,
  now: number,
  windowMs = 60_000
): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(key)) || 0;
    if (now - last < windowMs) return false;
    storage.setItem(key, String(now));
    return true;
  } catch {
    // Storage blocked (private mode, policy): never loop, never throw.
    return false;
  }
}
